import React, { useState, memo, useCallback } from 'react';
import Box from '@mui/material/Box';
import TableCell from '@mui/material/TableCell';
import IconButton from '@mui/material/IconButton';
import { MenuVerticalIcon } from '~/assets/icons';
import styles from './OpeningStatementList.module.scss';
import classNames from 'classnames/bind';
import BaseTable from '~/components/common/base-table/BaseTable';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { templateActions } from '~/thunks/template/templateSlice';
import { HeadCelType, SortDirection } from '~/utils/types/common';
import { useTranslation } from 'react-i18next';
import { ITemplate } from '~/utils/interface/template';
import usePermission from '~/hooks/usePermission';
import dayjs from 'dayjs';
import MenuOption from '../menu-option/MenuOption';
import { PERMISSIONS } from '~/utils/constants/permission';

const cx = classNames.bind(styles);

type OpeningStatementListProps = {
  onOpenEdit: (id: string) => void;
};

function OpeningStatementList(props: OpeningStatementListProps) {
  const { t } = useTranslation('admin/template');
  const headCells: Array<HeadCelType> = [
    {
      id: 'name',
      label: t(`properties.name`),
      sortable: true,
      searchable: true,
    },

    {
      id: 'createdAt',
      label: t(`properties.createdAt`),
      sortable: true,
      searchable: true,
    },
    {
      id: '',
      label: '',
      sortable: false,
      searchable: false,
    },
  ];
  const dispatch = useAppDispatch();
  const permissions = usePermission();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { templates, currentPage, totalPages, filter, menuSelectedId } = useAppSelector((state) => state.template);

  const openOptionMenu = (event: React.MouseEvent<HTMLButtonElement>, itemId: string) => {
    setAnchorEl(event.currentTarget);
    dispatch(templateActions.changeMenuSelectId(itemId));
  };
  const handleClose = () => {
    setAnchorEl(null);
    dispatch(templateActions.changeMenuSelectId(undefined));
  };

  const setSelectedIds = useCallback(
    (selected: Array<string>) => {
      dispatch(templateActions.changeSelectedIds(selected));
    },
    [dispatch]
  );

  const handleEditItem = () => {
    handleClose();
    if (menuSelectedId !== undefined) {
      props.onOpenEdit(menuSelectedId);
    }
  };

  const handleDeleteItem = () => {
    handleClose();
    if (menuSelectedId !== undefined) {
      dispatch(templateActions.changeMenuSelectId(menuSelectedId));
      dispatch(templateActions.changeOpenAlertDelete(true));
    }
  };

  const handleChangePage = (newPage: number) => {
    dispatch(
      templateActions.setFilter({
        page: newPage,
      })
    );
  };

  const handleRequestSort = (sortBy: Array<{ orderBy: string; order: SortDirection }>) => {
    dispatch(
      templateActions.setFilter({
        sort: sortBy.map((item) => ({
          field: item.orderBy,
          type: item.order,
        })),
      })
    );
  };

  return (
    <Box className={cx('term-condition-list')}>
      <BaseTable<ITemplate>
        data={templates}
        setSelectedIds={setSelectedIds}
        headCells={headCells}
        onChangePage={handleChangePage}
        onRequestSort={handleRequestSort}
        page={currentPage}
        totalPages={totalPages}
        sort={filter.sort}
        renderCell={(item: ITemplate) => {
          return (
            <>
              <TableCell component='th' scope='row' padding='none'>
                <Box className={cx('name')}>{item.name}</Box>
              </TableCell>

              <TableCell component='th' scope='row' padding='none'>
                <Box className={cx('createdAt')}>
                  {item?.createdAt ? dayjs(item.createdAt).format('DD MMM, YYYY') : ''}
                </Box>
              </TableCell>

              {(permissions[PERMISSIONS.openingStatementTemplate_edit] ||
                permissions[PERMISSIONS.openingStatementTemplate_delete]) && (
                <TableCell>
                  <Box className={cx('action-container')}>
                    <IconButton
                      onClick={(event) => {
                        openOptionMenu(event, item.id);
                      }}
                    >
                      <MenuVerticalIcon className='w-[19px] h-[19px]' />
                    </IconButton>
                  </Box>
                </TableCell>
              )}
            </>
          );
        }}
      />
      <MenuOption
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        handleEditItem={handleEditItem}
        handleDeleteItem={handleDeleteItem}
        enableEdit={permissions[PERMISSIONS.openingStatementTemplate_edit]}
        enableDelete={permissions[PERMISSIONS.openingStatementTemplate_delete]}
      />
    </Box>
  );
}

export default memo(OpeningStatementList);
